import React, { FC, useMemo, useState } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { DeliveryMethodOptions } from '@fabric-ui/components/Table';
import { ScheduleExport } from '@fabric-ui/icons';
import { iconSize } from '@fabric-ui/utils';
import { Badge, IconButton, useTheme } from '@mui/material';
import { useAvContext } from '../../../context/AvContextProvider';
import { Permission, PermissionEntitiesNames } from '../../../types';
import { cronExpressionToString } from '../../../utils/utils';
import { ReportsTypes } from '../../CustomDashboards/types';
import { useAvailableIntegrationInstance } from '../hooks';
import { emailIntegrationType, findIntegration } from '../reports.utils';
import { ExportDetails } from '../types';
import { ScheduleExportWindow } from './ScheduleExportWindow';

const scheduleExportIcon = <ScheduleExport style={{ ...iconSize(20), margin: '2px' }} />;

interface ExportButtonProps {
  initExportDetails: ExportDetails;
  setInitExportDetails: (prev: ExportDetails) => void;
  formatOptions?: string[];
  helperTexts?: { title: string; message: string };
  deliveryMethodsOptions?: DeliveryMethodOptions[];
  disabled?: boolean;
  warningMessage?: string;
  dashboardReportId: string;
  allowEdit?: boolean;
  isCompressAvailable?: boolean;
  type: ReportsTypes;
  scheduledBy?: string;
}

const ExportDialogButton: FC<ExportButtonProps> = ({
  initExportDetails,
  setInitExportDetails,
  formatOptions,
  helperTexts,
  disabled,
  deliveryMethodsOptions = Object.values(DeliveryMethodOptions),
  dashboardReportId,
  allowEdit = true,
  isCompressAvailable = false,
  type,
  warningMessage,
  scheduledBy,
}) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();
  const theme = useTheme();
  const hasPermissionsToIntegrations = hasAllowedPermissionToResource({
    resource: PermissionEntitiesNames.INTEGRATION,
    permission: Permission.READ,
  });
  const [open, setOpen] = useState(false);
  const cronString = cronExpressionToString(initExportDetails.intervalExpression);
  const isScheduleActive =
    initExportDetails.enabled && (initExportDetails.deliveryConfig?.integrationId || initExportDetails.deliveryConfig?.recipients?.length);

  const {
    data: availableIntegrationOptions = initExportDetails.deliveryConfig?.integrationId === emailIntegrationType.id
      ? [emailIntegrationType]
      : [
          emailIntegrationType,
          {
            id: initExportDetails.deliveryConfig?.integrationId,
            name: '',
            integrationType: initExportDetails.deliveryConfig?.type,
            redactedConfig: initExportDetails.deliveryConfig,
          },
        ],
  } = useAvailableIntegrationInstance(deliveryMethodsOptions, hasPermissionsToIntegrations);

  const exportDetails = useMemo(() => initExportDetails, [initExportDetails]);

  const selectedIntegrationInfo = useMemo(
    () => findIntegration(availableIntegrationOptions, exportDetails),
    [availableIntegrationOptions, exportDetails.deliveryConfig?.integrationId]
  );

  const scheduleButton = (
    <IconButton onClick={() => setOpen(true)} disabled={disabled} aria-label="Schedule Export">
      {scheduleExportIcon}
    </IconButton>
  );

  return (
    <>
      {isScheduleActive && cronString ? (
        <AvTooltip title={cronString}>
          <Badge
            badgeContent=""
            {...(selectedIntegrationInfo.active || !selectedIntegrationInfo.id
              ? { color: 'primary' }
              : {
                  sx: {
                    '& .MuiBadge-badge': { backgroundColor: theme.palette.colors.negative[400] },
                  },
                })}>
            {scheduleButton}
          </Badge>
        </AvTooltip>
      ) : (
        <AvTooltip title="Schedule Export">{scheduleButton}</AvTooltip>
      )}
      <ScheduleExportWindow
        initExportDetails={exportDetails}
        setInitExportDetails={setInitExportDetails}
        isOpen={open}
        setIsOpen={setOpen}
        formatOptions={formatOptions}
        helperTexts={helperTexts}
        dashboardReportId={dashboardReportId}
        allowEdit={allowEdit}
        isCompressAvailable={isCompressAvailable}
        availableIntegrationOptions={availableIntegrationOptions}
        type={type}
        warningMessage={warningMessage}
        scheduledBy={scheduledBy}
      />
    </>
  );
};

export default ExportDialogButton;
